import React, { useState, useEffect, useCallback, MouseEventHandler, useContext } from "react";
import styles from "./style.module.scss";
import { Text, Input, Icons } from "@evvve/ui-kit";
const { ChevronLeft, Search } = Icons;

import { useAppSelector } from "src/hooks/useStoreHooks";

import { ChatSocketIoContext } from "src/shared-features/Wrappers";

import ParticipantItem from "./features/ParticipantItem";
import ContextMenu, { ContextMenuTypes } from "src/shared-features/ContextMenu";
import { useGetMembersQuery } from "src/store/chat/api";
import { CurrentChatPage } from "../types";
import ViewProfile from "../ViewProfile";
import { applyLongPress } from "../helpers";

type Props = {
  dialogId: string;
  onBack?: () => void;
}

type Participant = {
  id: string;
  name?: string | null;
  avatarUrl?: string | null;
};

type UpdateUserMsg = { user_id: string; name: string; avatar_link: string };

export default function Participants({ dialogId, onBack }: Props) {
  const thisUserId = useAppSelector(state => state.user.userInfo?.unique_id);
  const chatSocket = useContext(ChatSocketIoContext);

  //states
  const [currentPage, setCurrentPage] = useState<CurrentChatPage>(CurrentChatPage.Participants);

  const [participants, setParticipants] = useState<Participant[] | null>(null);
  const [currentParticipantId, setCurrentParticipantId] = useState<string | null>(null);

  const [currentSearch, setCurrentSearch] = useState<string | null>(null);
  const [filteredParticipants, setFilteredParticipants] = useState<Participant[] | null>(null);

  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);

  //get participants [concern]
  const getMembersState = useGetMembersQuery({ chatId: dialogId });

  useEffect(() => {
    currentPage === CurrentChatPage.Participants &&
    !getMembersState.isUninitialized && getMembersState.refetch();
  }, [currentPage === CurrentChatPage.Participants]);

  //update participant data [concern][reflex]
  useEffect(() => {
    if (!chatSocket?.chatSocket)
      return;

    const updateParticipant = ({ user_id, name, avatar_link } : UpdateUserMsg) => (
      setParticipants(ps => (ps &&
        ps.map(p => p.id !== user_id ? p :
          {
            id: user_id,
            name,
            avatarUrl: avatar_link,
          })
      ))
    );

    chatSocket.chatSocket.on("userUpdate", updateParticipant);
    return () => {
      chatSocket.chatSocket?.off("userUpdate", updateParticipant);
    };

  }, [chatSocket?.chatSocket]);

  //participants [reflex]
  useEffect(() => {
    if (!getMembersState.currentData || getMembersState.isFetching)
      return;

    const psApi = getMembersState.currentData.members;
    const ps = psApi.map(p => ({
      id: p.id,
      name: p.data.name,
      avatarUrl: p.data.avatar_link,
    }));

    setParticipants(ps);

  }, [getMembersState]);

  //current serch participants [reflex]
  useEffect(() => {
    if (currentSearch) {
      participants && setFilteredParticipants(
        participants.filter(p => p.name && p.name.
          toLowerCase().
          includes(currentSearch.toLowerCase()))
      );
    }
  }, [currentSearch]);

  //create participant click handler [concern][reflex]
  const createOnParticipantClickHandler = useCallback<(participantId: string) => MouseEventHandler<HTMLLIElement>>
    ((participantId) => (event) => {
      event.preventDefault();

      if (!thisUserId || thisUserId === participantId)
        return;

      setCurrentParticipantId(participantId);
      setContextMenuPosition({
        left: event.clientX,
        top: event.clientY,
      });
      setIsContextMenuOpen(true);
    }, [thisUserId]);

  //create participant longpress handler [concern][reflex]
  const createOnParticipantLongPressHandler = useCallback<(participantId: string) => (event: TouchEvent) => void>
    ((participantId) => (event) => {
      if (!thisUserId || thisUserId === participantId)
        return;

      setCurrentParticipantId(participantId);
      setContextMenuPosition({
        left: event.touches[0].clientX,
        top: event.touches[0].clientY,
      });
      setIsContextMenuOpen(true);
    }, [thisUserId]);

  // close context menu if page scrolled or resized [concern][reflex]
  useEffect(() => {
    if (!isContextMenuOpen)
      return;

    const closeContextMenu = () => {
      setCurrentParticipantId(null);
      setIsContextMenuOpen(false);
    };

    document.addEventListener("scroll", closeContextMenu);
    window.addEventListener("resize", closeContextMenu);
    return () => {
      document.removeEventListener("scroll", closeContextMenu);
      window.removeEventListener("resize", closeContextMenu);
    };

  }, [isContextMenuOpen]);

  //

  const users = currentSearch ? filteredParticipants : participants;

  if (currentPage === CurrentChatPage.ViewProfile)
    if (!currentParticipantId)
      return null;
    else
      return <ViewProfile
        memberId={currentParticipantId}
        onClose={() => {
          setCurrentPage(CurrentChatPage.Participants);
          setCurrentParticipantId(null);
        }}
      />;

  if (currentPage !== CurrentChatPage.Participants)
    return null;

  return (
    <>
      <div className={[styles.header].join(" ")}>
        <div className={styles.backButtonAndTitle}>
          <button style={ { background: "none", border: "none" } } onClick={onBack} className={styles.backButton}>
            <ChevronLeft size="m" color="violet600" />
          </button>
          <Text size={["m", "m", "m", "m", 6]} bold className={styles.participantsTitle}>
            Participants
          </Text>
        </div>
        <div className={styles.search}>
          <div className={styles.search_input}>
            <Input
              onChange={(e) => setCurrentSearch(e.target.value)}
              value={currentSearch || ""}
              endAdornment={<Search color="grayscale600" size="m" />}
              placeholder="Search by name"
            />
          </div>
        </div>
      </div>
      {users &&
        <ul className={styles.participantsList}>
          { users.map(user => <li
            key={user.id}
            onContextMenu={createOnParticipantClickHandler(user.id)}
            ref={(node) => {
              //long press context menu
              const clickHandler = createOnParticipantLongPressHandler(user.id);
              node && applyLongPress(node, clickHandler);
            }}
            onClick={() => {
              if (!thisUserId || thisUserId === user.id)
                return;

              setCurrentParticipantId(user.id);
              setCurrentPage(CurrentChatPage.ViewProfile);
              setParticipants(null);
            }}
          >
            <ParticipantItem avatarUrl={user.avatarUrl} username={user.name} />
          </li>)}
        </ul>
      }
      {isContextMenuOpen && contextMenuPosition && (
        <ContextMenu
          onClickBlockingArea={() => {
            setIsContextMenuOpen(false);
            setCurrentParticipantId(null);
          }}
          position={{
            left: contextMenuPosition.left,
            top: contextMenuPosition.top,
          }}
          actions={[
            {
              type: ContextMenuTypes.ViewProfile,
              handler: () => {
                setCurrentPage(CurrentChatPage.ViewProfile);
                setParticipants(null);
                setIsContextMenuOpen(false);
              }
            }
          ]}
        />
      )}
    </>
  );
}